/**
 * ADF Renderer Component
 * 
 * Renders Atlassian Document Format (ADF) content as styled HTML.
 * Used for displaying JIRA comments in read-only mode.
 */
import React from 'react';
import { Box, Typography, Link as MuiLink, Chip, Avatar } from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { API_BASE_URL } from '../utils/apiConfig';

// JIRA server base URL for converting relative URLs
const JIRA_SERVER = 'https://centime.atlassian.net';

/**
 * Helper to create proxy URL for JIRA images
 * Handles both direct JIRA URLs and relative paths
 */
const createProxyUrl = (originalUrl) => {
  if (!originalUrl) return null;
  if (originalUrl.trim() === '') return null;
  
  let urlToProxy = originalUrl;
  
  // Handle relative URLs from JIRA
  if (originalUrl.startsWith('/rest/api/') || originalUrl.startsWith('/secure/')) {
    urlToProxy = JIRA_SERVER + originalUrl;
  } else if (originalUrl.includes('/rest/api/') && !originalUrl.includes('.atlassian.net')) {
    const pathMatch = originalUrl.match(/(\/rest\/api\/.*)/);
    if (pathMatch) {
      urlToProxy = JIRA_SERVER + pathMatch[1];
    }
  } else if (originalUrl.includes('/secure/attachment/') && !originalUrl.includes('.atlassian.net')) {
    const pathMatch = originalUrl.match(/(\/secure\/attachment\/.*)/);
    if (pathMatch) {
      urlToProxy = JIRA_SERVER + pathMatch[1];
    }
  }
  
  // Check if this is a JIRA URL that needs proxying
  const isJiraUrl = urlToProxy.includes('.atlassian.net') || 
                    urlToProxy.includes('/secure/attachment/') || 
                    urlToProxy.includes('/rest/api/') ||
                    urlToProxy.includes('/secure/thumbnail/') ||
                    urlToProxy.includes('/wiki/download/') ||
                    urlToProxy.includes('jira') ||
                    urlToProxy.includes('/attachment/');
  
  if (isJiraUrl) {
    try {
      // Base64 encode the URL for safe transmission
      const encodedUrl = btoa(urlToProxy);
      return `${API_BASE_URL}/api/production-tickets/proxy/image?url=${encodedUrl}`;
    } catch (e) {
      console.error('Failed to encode URL:', urlToProxy, e);
      return originalUrl;
    }
  }
  return originalUrl;
};

// Styles for rendered content
const rendererStyles = {
  container: {
    fontSize: '0.875rem',
    lineHeight: 1.6,
    color: 'text.primary',
    wordBreak: 'break-word',
    
    // Paragraphs
    '& p': {
      margin: '0 0 8px 0',
      '&:last-child': { margin: 0 },
    },
    
    // Headings
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      margin: '16px 0 8px 0',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    '& h1': { fontSize: '1.5rem' },
    '& h2': { fontSize: '1.25rem' },
    '& h3': { fontSize: '1.1rem' },
    '& h4': { fontSize: '1rem' },
    
    // Lists
    '& ul, & ol': {
      margin: '8px 0',
      paddingLeft: '24px',
    },
    '& li': {
      margin: '4px 0',
      '& > p': { margin: 0 },
    },
    
    // Blockquote
    '& blockquote': {
      borderLeft: '3px solid #dfe1e6',
      paddingLeft: '16px',
      margin: '8px 0',
      color: 'text.secondary',
    },
    
    // Code
    '& code': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
      borderRadius: '3px',
      padding: '2px 4px',
      fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
      fontSize: '0.85em',
    },
    '& pre': {
      backgroundColor: '#1e1e1e',
      borderRadius: '4px',
      padding: '12px 16px',
      margin: '8px 0',
      overflow: 'auto',
      '& code': {
        backgroundColor: 'transparent',
        padding: 0,
        color: '#d4d4d4',
        fontSize: '0.85rem',
      },
    },
    
    // Links
    '& a': {
      color: 'primary.main',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    
    // Images
    '& img': {
      maxWidth: '100%',
      height: 'auto',
      borderRadius: '4px',
      margin: '8px 0',
      display: 'block',
    },
    
    // Horizontal rule
    '& hr': {
      border: 'none',
      borderTop: '1px solid #dfe1e6',
      margin: '16px 0',
    },
    
    // Tables
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      margin: '8px 0',
    },
    '& th, & td': {
      border: '1px solid #dfe1e6',
      padding: '8px 12px',
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: '#f4f5f7',
      fontWeight: 600,
    },
    
    // Panels (info, warning, error boxes)
    '& .panel': {
      borderRadius: '4px',
      padding: '12px 16px',
      margin: '8px 0',
    },
    '& .panel-info': {
      backgroundColor: '#deebff',
      borderLeft: '4px solid #0052cc',
    },
    '& .panel-warning': {
      backgroundColor: '#fffae6',
      borderLeft: '4px solid #ff8b00',
    },
    '& .panel-error': {
      backgroundColor: '#ffebe6',
      borderLeft: '4px solid #de350b',
    },
    '& .panel-success': {
      backgroundColor: '#e3fcef',
      borderLeft: '4px solid #00875a',
    },
    '& .panel-note': {
      backgroundColor: '#eae6ff',
      borderLeft: '4px solid #6554c0',
    },
  },
};

/**
 * Render a mention as an inline element
 */
const MentionRenderer = ({ mention }) => {
  const displayName = mention.attrs?.text || mention.attrs?.displayName || 'Unknown';
  const cleanName = displayName.replace(/^@/, '');
  
  return (
    <Chip
      size="small"
      avatar={
        <Avatar sx={{ width: 16, height: 16, fontSize: '0.65rem' }}>
          {cleanName[0]?.toUpperCase() || <PersonIcon sx={{ fontSize: 12 }} />}
        </Avatar>
      }
      label={cleanName}
      sx={{
        height: 20,
        fontSize: '0.75rem',
        backgroundColor: 'rgba(25, 118, 210, 0.08)',
        color: 'primary.main',
        fontWeight: 500,
        '& .MuiChip-avatar': {
          width: 16,
          height: 16,
        },
        '& .MuiChip-label': {
          px: 0.5,
        },
      }}
      component="span"
    />
  );
};

/**
 * Render inline content (text with marks, mentions, etc.)
 */
const renderInlineContent = (nodes, imageUrlResolver) => {
  if (!nodes || !Array.isArray(nodes)) return null;

  return nodes.map((node, index) => {
    const key = `inline-${index}`;

    switch (node.type) {
      case 'text':
        return renderTextWithMarks(node, key);
      
      case 'mention':
        return <MentionRenderer key={key} mention={node} />;
      
      case 'emoji':
        return <span key={key}>{node.attrs?.text || node.attrs?.shortName || '😀'}</span>;
      
      case 'hardBreak':
        return <br key={key} />;
      
      case 'inlineCard':
        return (
          <MuiLink 
            key={key} 
            href={node.attrs?.url} 
            target="_blank" 
            rel="noopener noreferrer"
          >
            {node.attrs?.url || 'Link'}
          </MuiLink>
        );
      
      default:
        if (node.text) {
          return <span key={key}>{node.text}</span>;
        }
        return null;
    }
  });
};

/**
 * Render text node with marks (bold, italic, etc.)
 */
const renderTextWithMarks = (node, key) => {
  let element = <>{node.text}</>;

  if (node.marks && Array.isArray(node.marks)) {
    for (const mark of node.marks) {
      switch (mark.type) {
        case 'strong':
          element = <strong>{element}</strong>;
          break;
        case 'em':
          element = <em>{element}</em>;
          break;
        case 'underline':
          element = <u>{element}</u>;
          break;
        case 'strike':
          element = <s>{element}</s>;
          break;
        case 'code':
          element = <code>{element}</code>;
          break;
        case 'link':
          element = (
            <MuiLink 
              href={mark.attrs?.href} 
              target="_blank" 
              rel="noopener noreferrer"
            >
              {element}
            </MuiLink>
          );
          break;
        case 'textColor':
          element = <span style={{ color: mark.attrs?.color }}>{element}</span>;
          break;
        case 'subsup':
          if (mark.attrs?.type === 'sub') {
            element = <sub>{element}</sub>;
          } else {
            element = <sup>{element}</sup>;
          }
          break;
        default:
          break;
      }
    }
  }

  return <span key={key}>{element}</span>;
};

/**
 * Render a block-level ADF node
 */
const renderBlockNode = (node, index, imageUrlResolver) => {
  const key = `block-${index}`;

  switch (node.type) {
    case 'paragraph':
      return (
        <p key={key}>
          {renderInlineContent(node.content, imageUrlResolver)}
        </p>
      );
    
    case 'heading':
      const HeadingTag = `h${node.attrs?.level || 1}`;
      return (
        <HeadingTag key={key}>
          {renderInlineContent(node.content, imageUrlResolver)}
        </HeadingTag>
      );
    
    case 'bulletList':
      return (
        <ul key={key}>
          {(node.content || []).map((item, i) => (
            <li key={i}>
              {(item.content || []).map((child, j) => renderBlockNode(child, j, imageUrlResolver))}
            </li>
          ))}
        </ul>
      );
    
    case 'orderedList':
      return (
        <ol key={key} start={node.attrs?.order || 1}>
          {(node.content || []).map((item, i) => (
            <li key={i}>
              {(item.content || []).map((child, j) => renderBlockNode(child, j, imageUrlResolver))}
            </li>
          ))}
        </ol>
      );
    
    case 'codeBlock':
      const code = (node.content || [])
        .filter(n => n.type === 'text')
        .map(n => n.text)
        .join('');
      const language = node.attrs?.language || '';
      return (
        <pre key={key} data-language={language}>
          <code>{code}</code>
        </pre>
      );
    
    case 'blockquote':
      return (
        <blockquote key={key}>
          {(node.content || []).map((child, i) => renderBlockNode(child, i, imageUrlResolver))}
        </blockquote>
      );
    
    case 'rule':
      return <hr key={key} />;
    
    case 'mediaSingle':
    case 'mediaGroup':
      return renderMedia(node, key, imageUrlResolver);
    
    case 'panel':
      const panelType = node.attrs?.panelType || 'info';
      return (
        <div key={key} className={`panel panel-${panelType}`}>
          {(node.content || []).map((child, i) => renderBlockNode(child, i, imageUrlResolver))}
        </div>
      );
    
    case 'table':
      return renderTable(node, key, imageUrlResolver);
    
    default:
      // Try to render content if present
      if (node.content) {
        return (
          <div key={key}>
            {node.content.map((child, i) => renderBlockNode(child, i, imageUrlResolver))}
          </div>
        );
      }
      return null;
  }
};

/**
 * Check if a string looks like a numeric ID (for attachment API) vs UUID (Media Services)
 */
const isNumericId = (id) => /^\d+$/.test(id);

/**
 * Render media (images, videos)
 */
const renderMedia = (node, key, imageUrlResolver) => {
  const media = node.content?.find(n => n.type === 'media') || (node.type === 'media' ? node : null);
  if (!media) return null;

  const attrs = media.attrs || {};
  let src = '';
  
  // Debug logging
  console.log('[AdfRenderer] renderMedia attrs:', JSON.stringify(attrs));
  
  // Priority 1: If we have a URL, use it (but proxy JIRA URLs)
  if (attrs.url) {
    console.log('[AdfRenderer] Using attrs.url:', attrs.url);
    src = createProxyUrl(attrs.url);
  }
  
  // Priority 2: If we have an attachment ID
  if (!src && attrs.id) {
    console.log('[AdfRenderer] Processing attrs.id:', attrs.id, 'type:', attrs.type, 'collection:', attrs.collection);
    
    if (isNumericId(attrs.id)) {
      // Numeric ID - use the attachment proxy endpoint directly
      console.log('[AdfRenderer] Using numeric attachment ID');
      src = `${API_BASE_URL}/api/production-tickets/proxy/attachment/${attrs.id}`;
    } else if (imageUrlResolver) {
      // UUID - try the imageUrlResolver (which might map to attachments)
      console.log('[AdfRenderer] Using imageUrlResolver for UUID');
      src = imageUrlResolver(attrs.id);
    } else {
      // Fallback: try the attachment content API with the ID
      console.log('[AdfRenderer] Fallback: building attachment URL');
      const attachmentUrl = `${JIRA_SERVER}/rest/api/3/attachment/content/${attrs.id}`;
      src = createProxyUrl(attachmentUrl);
    }
  }

  // Handle external vs file media
  if (attrs.type === 'file' && !src) {
    // This is a JIRA attachment reference that we couldn't resolve
    console.log('[AdfRenderer] Could not resolve file attachment:', attrs.id);
    return (
      <Box key={key} sx={{ my: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          📎 Attachment: {attrs.alt || attrs.id || 'unknown'}
        </Typography>
      </Box>
    );
  }

  // If we still don't have a src, don't render anything
  if (!src) {
    console.log('[AdfRenderer] No src found, skipping render');
    return null;
  }
  
  console.log('[AdfRenderer] Final image src:', src);

  return (
    <Box key={key} sx={{ my: 1 }}>
      <img 
        src={src} 
        alt={attrs.alt || 'Image'} 
        title={attrs.title || ''} 
        loading="lazy"
        style={{ maxWidth: '100%', height: 'auto', borderRadius: '4px' }}
        onError={(e) => {
          console.error('[AdfRenderer] Failed to load image:', src, 'attrs:', attrs);
          // Show placeholder on error
          e.target.style.display = 'none';
          e.target.parentNode.innerHTML = `<span style="color: #666; font-size: 0.8rem;">📷 Image could not be loaded</span>`;
        }}
      />
    </Box>
  );
};

/**
 * Render table
 */
const renderTable = (node, key, imageUrlResolver) => {
  return (
    <table key={key}>
      <tbody>
        {(node.content || []).map((row, rowIndex) => (
          <tr key={rowIndex}>
            {(row.content || []).map((cell, cellIndex) => {
              const CellTag = cell.type === 'tableHeader' ? 'th' : 'td';
              return (
                <CellTag 
                  key={cellIndex}
                  colSpan={cell.attrs?.colspan || 1}
                  rowSpan={cell.attrs?.rowspan || 1}
                >
                  {(cell.content || []).map((child, i) => renderBlockNode(child, i, imageUrlResolver))}
                </CellTag>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/**
 * ADF Renderer Component
 * 
 * @param {Object} props
 * @param {Object} props.content - ADF document object
 * @param {Function} props.imageUrlResolver - Function to resolve attachment IDs to URLs
 */
const AdfRenderer = ({ content, imageUrlResolver }) => {
  if (!content || content.type !== 'doc' || !content.content) {
    return null;
  }

  return (
    <Box sx={rendererStyles.container}>
      {content.content.map((node, index) => renderBlockNode(node, index, imageUrlResolver))}
    </Box>
  );
};

export default AdfRenderer;
